/**
 * Health: treatments and vaccinations (product, dose, route, lot, withdrawal), procedures, sickness,
 * diagnostics and lab results, breeding soundness exams, protocols and booster rounds, and the
 * withdrawal holds that follow from them. Medicine and vaccine are this module's stock kinds.
 *
 * Other modules read `animal.facets.health` (see HealthState) and should use `activeHolds(state, today)`
 * rather than trusting `holds` as of the last recompute.
 */
import { addDays, defineEvent, defineFacet, defineModule, f, msg, type Badge, type Fact, type FacetEvent, type Fields, type FollowUp, type Formatters, type Quantity, type SubjectRef } from "../../contract";
import {
  BSE_CLASSES, DIAGNOSIS, DOSE_UNITS, METHODS, PASS_FAIL, PROCEDURES, RESULTS, RESULT_STATUS, ROUTES, SAMPLE_TYPES, SEVERITY, SICK_REASONS, SICK_STATUS, SITES, SYMPTOMS, TESTS,
  UNRESOLVED_WINDOW_DAYS, WITHDRAWAL_SOURCE, activeHolds, daysLeft, holdsFromDose, longestHold, monthDay, productLabelOf,
  type BseData, type HealthHold, type HealthState, type LabResultData, type ProcedureData, type SampleData, type SicknessData, type TreatmentData,
} from "./shared/logic";

export * from "./shared/logic";

export const HEALTH_TYPES = {
  treatment: "health.treatment",
  vaccination: "health.vaccination",
  procedure: "health.procedure",
  sickness: "health.sickness",
  sample: "health.sample",
  result: "health.lab_result",
  bse: "health.bse",
} as const;

// ---------------------------------------------------------------- shared pieces

const animals = (x: Formatters, subjects: SubjectRef[]): string => {
  const list = subjects.filter((s) => s.kind === "animal");
  if (list.length === 1) return list[0].label ?? x.label(list[0].id);
  return x.count(list.length, "{{count}} head", "{{count}} head");
};

const productOf = (x: Formatters, d: { product_id?: string; lot_id?: string; product_name?: string }): string =>
  d.product_id ? x.label(d.product_id) : d.product_name?.trim() ? d.product_name : d.lot_id ? x.label(d.lot_id) : x.t("Unknown product");

const doseText = (x: Formatters, d: TreatmentData): string =>
  [d.dose ? x.quantity(d.dose) : null, d.route ? x.t(ROUTES[d.route]) : null, d.site ? x.t(SITES[d.site]).toLowerCase() : null].filter(Boolean).join(" ");

const withdrawalText = (x: Formatters, d: TreatmentData): string | null => {
  if (typeof d.meat_withdrawal_days === "number") {
    return d.meat_withdrawal_days === 0 ? x.t("no meat withdrawal") : x.count(d.meat_withdrawal_days, "{{count}}-day meat withdrawal", "{{count}}-day meat withdrawal");
  }
  return d.product_id || d.product_name || d.lot_id ? x.t("withdrawal not recorded") : null;
};

const animalSubjects = [
  { role: "animal", kind: "animal" as const, label: "Animals", many: true, required: true },
  { role: "group", kind: "group" as const, label: "Group", help: "The group they were in that day. Every animal is still listed." },
];

/** Fields shared by treatments and vaccinations. */
function doseFields(kind: "treatment" | "vaccination"): Fields {
  return {
    product_id: f.ref("Product", "stock_item", {
      filter: { kind: kind === "vaccination" ? ["vaccine", "medicine"] : ["medicine", "vaccine"] },
      agent: "stock_item id from health_products or find_records. Leave empty when the product isn't in supplies and use product_name.",
    }),
    product_name: f.text("Product name", {
      level: "more",
      help: "As it was said or written. Filled in for you when you pick a product.",
      agent: "Always set: the product's name as listed in supplies, or exactly as spoken when it isn't in supplies.",
    }),
    dose: f.quantity("Dose each", "dose", {
      units: DOSE_UNITS,
      agent: "Amount given to EACH animal exactly as said (\"5cc\" is {value:5, unit:\"cc\"}). Never work out or invent a dose.",
    }),
    route: f.enum("Route", ROUTES, { agent: "IM in the muscle, SQ under the skin. Only when said or when it is the product's label route (then mark evidence default)." }),
    site: f.enum("Where on the animal", SITES, { level: "more", agent: "Body site such as \"in the neck\". Not a pasture or pen." }),
    lot_id: f.ref("Bottle or lot", "stock_lot", { level: "more", agent: "stock_lot id when the bottle or lot number is said, or when the product has only one lot on hand." }),
    ...(kind === "treatment" ? {
      reason: f.text("Reason", { placeholder: "Pinkeye, foot rot, pneumonia…", agent: "Diagnosis or reason in the speaker's words, e.g. pinkeye, foot rot, deworming." }),
      diagnosis: f.enum("Diagnosis", DIAGNOSIS, { level: "more" }),
      temperature: f.quantity("Temperature", "temperature", { level: "more" }),
      recheck_on: f.date("Recheck", { level: "more", help: "Hondo adds a task to look at them again.", agent: "Only when they say when to check again." }),
    } : {}),
    meat_withdrawal_days: f.int("Meat withdrawal (days)", {
      level: "more", min: 0, max: 400,
      help: "From the label or your vet. Copied from the product when you pick it.",
      agent: "Copy the product's label meat_withdrawal_days from health_products unless the speaker gives a vet's different instruction; mark evidence linked. 0 means none. Leave empty when unknown.",
    }),
    milk_withdrawal_hours: f.int("Milk withdrawal (hours)", { level: "more", min: 0, max: 2000, agent: "Copy the product's label milk_withdrawal_hours when it has one. Leave empty when unknown." }),
    withdrawal_source: f.enum("Withdrawal from", WITHDRAWAL_SOURCE, { level: "more" }),
    booster_on: f.date(kind === "vaccination" ? "Booster due" : "Give again", {
      level: kind === "vaccination" ? "core" : "more",
      help: "Hondo adds a task for that day.",
      agent: "Only when they say when, or the product has booster_days: the shot date plus booster_days (mark evidence default).",
    }),
    given_by: f.ref("Given by", "member", { level: "more" }),
    vet_id: f.ref("Vet", "contact", { level: "more", help: "The vet who prescribed or gave it." }),
    dose_weight: f.quantity("Weight used for the dose", "weight", { level: "more" }),
    use: f.quantity("Total used", "dose", {
      level: "more", units: DOSE_UNITS,
      help: "Hondo works this out from the dose and head count. Change it when some was spilled or the amounts differed.",
      agent: "Leave empty unless they say the total amount used.",
    }),
    protocol: f.text("Protocol", { level: "more", agent: "Name of the saved protocol used, when they used one." }),
    notes: f.longText("Notes", { level: "more", agent: "Keep the speaker's wording and language." }),
  };
}

function summarizeDose(e: { data: TreatmentData; subjects: SubjectRef[] }, x: Formatters): string {
  const d = e.data;
  const first = [productOf(x, d), doseText(x, d)].filter(Boolean).join(" ");
  return [first, animals(x, e.subjects), d.reason, withdrawalText(x, d)].filter(Boolean).join(" · ");
}

// Follow-up tasks (boosters, rechecks, retests). Keys are stable so a correction moves the task. A
// single animal is the task's subject (shown beside the title); a group task names the head count.
const animalList = (subjects: SubjectRef[]) => subjects.filter((s) => s.kind === "animal");
const onlyAnimal = (subjects: SubjectRef[]) => {
  const list = animalList(subjects);
  return list.length === 1 ? { kind: "animal" as const, id: list[0].id } : undefined;
};
const recheckTitle = (subjects: SubjectRef[]) => {
  const list = animalList(subjects);
  return list.length === 1 ? msg("Recheck") : msg("Recheck: {{count}} head", { count: list.length });
};

function doseFollowUps(d: TreatmentData, _on: string | null, subjects: SubjectRef[]): FollowUp[] {
  const out: FollowUp[] = [];
  const list = animalList(subjects);
  if (d.booster_on) {
    const product = productLabelOf(d) || "?";
    out.push({
      key: "booster", due_at: d.booster_on, subject: onlyAnimal(subjects),
      title: list.length === 1 ? msg("Booster: {{product}}", { product }) : msg("Booster {{product}}: {{count}} head", { product, count: list.length }),
    });
  }
  if (d.recheck_on) out.push({ key: "recheck", due_at: d.recheck_on, subject: onlyAnimal(subjects), title: recheckTitle(subjects) });
  return out;
}

function positiveQuantities(d: Record<string, unknown>, keys: string[]) {
  const issues = [];
  for (const k of keys) {
    const q = d[k] as Quantity | undefined;
    if (q && !(q.value > 0)) issues.push({ path: k, message: "Must be more than zero" });
  }
  return issues;
}

// ---------------------------------------------------------------- events

const treatment = defineEvent<TreatmentData>({
  type: HEALTH_TYPES.treatment,
  label: "Treatment",
  description: "Medicine actually given to one or more animals: antibiotics, pain relief, dewormers and pour-ons.",
  icon: "pill",
  tone: "health",
  subjects: animalSubjects,
  fields: doseFields("treatment"),
  summary: (e, x) => summarizeDose(e, x),
  review: "always",
  effects: { followUps: doseFollowUps },
  agent: {
    when: "Medicine was actually given: antibiotic, anti-inflammatory, dewormer, pour-on, injection or bolus, for one animal or a whole group (every animal is a subject, dose is per head).",
    avoid: "Not for vaccines (health.vaccination). Not for a plan or reminder to treat (task). Symptoms with nothing given are health.sickness. \"17 has foot rot, gave her Draxxin\" is two items: health.sickness and health.treatment.",
    examples: ["gave 42 5cc LA-200 in the neck for pinkeye", "17 has foot rot, gave her Draxxin", "poured the calves with Dectomax", "wormed the heifers"],
  },
  validate: (d) => positiveQuantities(d as Record<string, unknown>, ["dose", "use", "dose_weight"]),
});

const vaccination = defineEvent<TreatmentData>({
  type: HEALTH_TYPES.vaccination,
  label: "Vaccination",
  description: "Vaccine given to one or more animals.",
  icon: "syringe",
  tone: "health",
  subjects: animalSubjects,
  fields: doseFields("vaccination"),
  summary: (e, x) => summarizeDose(e, x),
  review: "always",
  effects: { followUps: doseFollowUps },
  agent: {
    when: "A vaccine was actually given (shots such as Vision 7, Bovi-Shield, blackleg, 7-way, respiratory, scours or pinkeye vaccines), to one animal or a group. List every animal; dose is per head.",
    avoid: "Not for antibiotics or dewormers (health.treatment). A planned or upcoming booster is a task, not a vaccination.",
    examples: ["vaccinated the replacement heifers with Vision 7", "gave the calves their weaning shots", "Bovi-Shield on the spring cows, 2 mL IM"],
  },
  validate: (d) => positiveQuantities(d as Record<string, unknown>, ["dose", "use", "dose_weight"]),
});

const procedure = defineEvent<ProcedureData>({
  type: HEALTH_TYPES.procedure,
  label: "Procedure",
  description: "Castration, dehorning, branding, implants, ear notches and hoof trims.",
  icon: "scissors",
  tone: "health",
  subjects: animalSubjects,
  fields: {
    kind: f.enum("What was done", PROCEDURES, { required: true }),
    method: f.enum("How", METHODS, { agent: "Castration: band, knife or burdizzo. Dehorning: paste, hot_iron, scoop or tipping. Branding: hot_iron or freeze. Only when said." }),
    brand: f.text("Brand and spot", { level: "more", placeholder: "Lazy C on the left hip" }),
    product_id: f.ref("Implant or product", "stock_item", { level: "more", filter: { kind: ["medicine", "vaccine"] } }),
    product_name: f.text("Product name", { level: "more" }),
    lot_id: f.ref("Bottle or lot", "stock_lot", { level: "more" }),
    meat_withdrawal_days: f.int("Meat withdrawal (days)", { level: "more", min: 0, max: 400 }),
    given_by: f.ref("Done by", "member", { level: "more" }),
    notes: f.longText("Notes", { level: "more" }),
  },
  summary: (e, x) => {
    const d = e.data;
    const what = d.kind === "other" && d.notes ? d.notes.split("\n")[0] : x.t(PROCEDURES[d.kind] ?? d.kind);
    return [what, d.method ? x.t(METHODS[d.method]).toLowerCase() : null, d.product_id ? x.label(d.product_id) : d.product_name, animals(x, e.subjects), d.brand].filter(Boolean).join(" · ");
  },
  agent: {
    when: "A procedure was done: castration, dehorning, branding, implanting, ear notching, hoof trimming. List every animal it was done to.",
    avoid: "Ear tags and retags belong to herd. An ear notch sent to a lab is also a health.sample.",
    examples: ["castrated the bull calves", "banded 812P and 58R", "dehorned the three heifers with paste", "trimmed 42's feet"],
  },
});

const sickness = defineEvent<SicknessData>({
  type: HEALTH_TYPES.sickness,
  label: "Sickness",
  description: "A sick or hurt animal, a recheck, or a recovery.",
  icon: "thermometer",
  tone: "health",
  subjects: [{ role: "animal", kind: "animal", label: "Animals", many: true, required: true }],
  fields: {
    condition: f.text("What's wrong", { placeholder: "Pinkeye, foot rot, pneumonia…", agent: "Condition in the speaker's words. Leave empty when they only describe symptoms." }),
    symptoms: f.multiEnum("Signs", SYMPTOMS),
    status: f.enum("Status", SICK_STATUS, { help: "Pick Recovered when a recheck finds them well.", agent: "sick unless they say the animal is better (improving) or well again (recovered)." }),
    severity: f.enum("How bad", SEVERITY, { level: "more" }),
    temperature: f.quantity("Temperature", "temperature", { level: "more" }),
    pulled: f.bool("Pulled to the sick pen", { level: "more" }),
    pen_id: f.ref("Pen", "place", { level: "more", agent: "Only when they say where the animal was put. Record the move itself with the land module's move." }),
    recheck_on: f.date("Recheck", { level: "more", help: "Hondo adds a task to look at them again." }),
    notes: f.longText("Notes", { level: "more", agent: "Keep the speaker's wording and language." }),
  },
  summary: (e, x) => {
    const d = e.data;
    const status = d.status === "recovered" ? x.t("Recovered") : d.status === "improving" ? x.t("Getting better") : null;
    const what = d.condition ?? (d.symptoms?.length ? x.list(d.symptoms.map((s) => x.t(SYMPTOMS[s] ?? s).toLowerCase())) : null);
    return [animals(x, e.subjects), status, what, d.severity && d.status !== "recovered" ? x.t(SEVERITY[d.severity]).toLowerCase() : null,
      d.temperature ? x.quantity(d.temperature) : null, d.pulled ? (d.pen_id ? x.t("pulled to {{pen}}", { pen: x.label(d.pen_id) }) : x.t("pulled")) : null].filter(Boolean).join(" · ");
  },
  effects: { followUps: (d, _on, subjects) => (d.recheck_on && d.status !== "recovered" ? [{ key: "recheck", due_at: d.recheck_on, subject: onlyAnimal(subjects), title: recheckTitle(subjects) }] : []) },
  agent: {
    when: "An animal is sick, hurt, lame, or off; a recheck of a sick animal; or a sick animal is better or recovered.",
    avoid: "Medicine given is a separate health.treatment. A dead animal is herd's death record.",
    examples: ["17 has foot rot", "42 has pinkeye in her right eye", "the 2503 heifer is coughing, pulled her to pen 2", "17 is walking better"],
  },
});

const sample = defineEvent<SampleData>({
  type: HEALTH_TYPES.sample,
  label: "Lab sample",
  description: "A sample taken for a lab test: blood, ear notch, manure, swab.",
  icon: "testTube",
  tone: "health",
  subjects: [{ role: "animal", kind: "animal", label: "Animals", many: true, required: true }],
  fields: {
    test: f.enum("Test", TESTS, { required: true }),
    test_name: f.text("Test name", { level: "more", help: "When it isn't in the list, or the lab's own name for it." }),
    sample_type: f.enum("Sample", SAMPLE_TYPES),
    lab_id: f.ref("Lab", "contact"),
    sample_id: f.text("Sample or barcode number", { level: "more" }),
    collected_by: f.ref("Collected by", "member", { level: "more" }),
    reason: f.text("Why", { level: "more" }),
    notes: f.longText("Notes", { level: "more" }),
  },
  summary: (e, x) => [x.t(TESTS[e.data.test] ?? e.data.test), e.data.sample_type ? x.t(SAMPLE_TYPES[e.data.sample_type]).toLowerCase() : null, animals(x, e.subjects), e.data.lab_id ? x.label(e.data.lab_id) : null].filter(Boolean).join(" · "),
  agent: {
    when: "Samples were actually taken or sent to a lab (ear notches for BVD, blood for Johne's or anaplasmosis, manure for a fecal egg count).",
    avoid: "A result coming back is health.lab_result. Planning to sample is a task.",
    examples: ["pulled ear notches on the calves for BVD", "drew blood on 110 for Johne's, sent to TVMDL"],
  },
});

const labResult = defineEvent<LabResultData>({
  type: HEALTH_TYPES.result,
  label: "Lab result",
  description: "A result from the lab for one or more animals.",
  icon: "lab",
  tone: "health",
  subjects: [{ role: "animal", kind: "animal", label: "Animals", many: true, required: true }],
  fields: {
    test: f.enum("Test", TESTS, { required: true }),
    result: f.enum("Result", RESULTS, { agent: "Exactly as reported. Never infer negative from silence." }),
    value: f.number("Value", { level: "more" }),
    unit: f.text("Unit", { level: "more", placeholder: "EPG, %, S/P" }),
    status: f.enum("Report", RESULT_STATUS, { level: "more" }),
    test_name: f.text("Test name", { level: "more" }),
    lab_id: f.ref("Lab", "contact", { level: "more" }),
    sample_id: f.text("Sample or barcode number", { level: "more" }),
    accession: f.text("Lab case number", { level: "more" }),
    reported_on: f.date("Reported", { level: "more" }),
    interpretation: f.longText("What the lab or vet said", { level: "more" }),
    notes: f.longText("Notes", { level: "more" }),
  },
  summary: (e, x) => [x.t(TESTS[e.data.test] ?? e.data.test), e.data.result ? x.t(RESULTS[e.data.result]).toLowerCase() : null,
    e.data.value !== undefined ? `${e.data.value}${e.data.unit ? ` ${e.data.unit}` : ""}` : null, animals(x, e.subjects),
    e.data.status && e.data.status !== "final" ? x.t(RESULT_STATUS[e.data.status]).toLowerCase() : null].filter(Boolean).join(" · "),
  agent: {
    when: "A lab or vet reported a test result (BVD, Johne's, trich, fecal egg count, blood work).",
    avoid: "A breeding soundness exam is health.bse. A pregnancy check belongs to breeding.",
    examples: ["BVD came back negative on all the calves", "110's Johne's test was negative"],
  },
});

const bse = defineEvent<BseData>({
  type: HEALTH_TYPES.bse,
  label: "Breeding soundness exam",
  description: "A bull's breeding soundness exam: scrotal size, semen quality, physical soundness and the vet's classification.",
  icon: "microscope",
  tone: "health",
  subjects: [{ role: "bull", kind: "animal", label: "Bull", required: true }],
  fields: {
    classification: f.enum("Result", BSE_CLASSES, { required: true, agent: "The examiner's classification. \"Passed\" is satisfactory; \"failed\" is unsatisfactory; \"come back\" or \"retest\" is deferred." }),
    scrotal: f.quantity("Scrotal circumference", "length", { units: ["cm", "in"] }),
    motility: f.int("Motility", { min: 0, max: 100, suffix: "%" }),
    morphology: f.int("Normal sperm", { min: 0, max: 100, suffix: "%" }),
    physical: f.enum("Feet, legs, eyes and sheath", PASS_FAIL, { level: "more" }),
    primary_defects: f.int("Primary defects", { level: "more", min: 0, max: 100, suffix: "%" }),
    secondary_defects: f.int("Secondary defects", { level: "more", min: 0, max: 100, suffix: "%" }),
    vet_id: f.ref("Vet", "contact", { agent: "The examining veterinarian or clinic contact." }),
    examiner: f.text("Examiner", { level: "more", help: "When the examiner isn't in your contacts." }),
    recheck_on: f.date("Retest", { level: "more", help: "Hondo adds a task for the retest." }),
    notes: f.longText("Notes", { level: "more" }),
  },
  summary: (e, x) => {
    const d = e.data;
    const bull = e.subjects.find((s) => s.role === "bull");
    return [bull ? bull.label ?? x.label(bull.id) : null, x.t(BSE_CLASSES[d.classification]), d.scrotal ? x.quantity(d.scrotal) : null,
      d.motility !== undefined ? x.t("{{pct}}% motility", { pct: d.motility }) : null, d.morphology !== undefined ? x.t("{{pct}}% normal", { pct: d.morphology }) : null,
      d.vet_id ? x.label(d.vet_id) : d.examiner].filter(Boolean).join(" · ");
  },
  agent: {
    when: "A bull had a breeding soundness exam or semen test, with the vet's result and any measurements.",
    avoid: "A scrotal measurement taken alone at weaning or yearling belongs to performance.",
    examples: ["Dr. Ramirez semen tested B31, passed, 38 cm", "B20 was deferred, retest in 60 days"],
  },
  validate: (d) => (d.scrotal && !(d.scrotal.value > 0) ? [{ path: "scrotal", message: "Must be more than zero" }] : []),
  effects: {
    followUps: (d, _on, subjects) => {
      const bull = subjects.find((s) => s.role === "bull");
      return d.recheck_on && bull ? [{ key: "retest", due_at: d.recheck_on, subject: { kind: "animal" as const, id: bull.id }, title: msg("Breeding soundness retest") }] : [];
    },
  },
});

// ---------------------------------------------------------------- facet

const dateOf = (e: FacetEvent): string => (e.occurred_at && e.occurred_precision !== "unknown" ? e.occurred_at.slice(0, 10) : "");

export function reduceHealth(events: FacetEvent[], today: string): HealthState | null {
  if (!events.length) return null;
  const holds = new Map<string, HealthHold>();
  const unresolved: HealthState["unresolved"] = [];
  const vaccinations = new Map<string, HealthState["vaccinations"][number]>();
  const procedures = new Map<string, HealthState["procedures"][number]>();
  const samples: Array<HealthState["samples_waiting"][number] & { matched?: boolean }> = [];
  const state: HealthState = { holds: [], unresolved: [], vaccinations: [], treatments: 0, procedures: [], samples_waiting: [] };
  for (const e of events) {
    const on = dateOf(e);
    switch (e.type) {
      case HEALTH_TYPES.treatment:
      case HEALTH_TYPES.vaccination: {
        const d = e.data as TreatmentData;
        const product = productLabelOf(d);
        if (e.type === HEALTH_TYPES.treatment) state.treatments++;
        if (on && (!state.last_treatment || on >= state.last_treatment.on)) state.last_treatment = { on, product, event_id: e.id };
        if (e.type === HEALTH_TYPES.vaccination) {
          const key = d.product_id ?? product.toLowerCase();
          const prev = vaccinations.get(key);
          if (!prev || on >= prev.on) vaccinations.set(key, { product, product_id: d.product_id, on, booster_on: d.booster_on, event_id: e.id });
        }
        for (const h of holdsFromDose({ id: e.id, on, data: d })) {
          const key = `${h.kind}:${d.product_id ?? product.toLowerCase()}`;
          const prev = holds.get(key);
          if (!prev || h.until >= prev.until) holds.set(key, h);
        }
        const recent = on ? addDays(on, UNRESOLVED_WINDOW_DAYS) > today : true;
        if (d.meat_withdrawal_days === undefined && recent && (d.product_id || product || d.lot_id)) unresolved.push({ product, product_id: d.product_id, on, event_id: e.id });
        if (state.sickness && on >= state.sickness.since && on > state.sickness.checked) state.sickness.checked = on;
        break;
      }
      case HEALTH_TYPES.procedure: {
        const d = e.data as ProcedureData;
        procedures.set(d.kind, { kind: d.kind, on, method: d.method, event_id: e.id });
        if (d.kind === "castration") state.castrated_on = on || state.castrated_on;
        for (const h of holdsFromDose({ id: e.id, on, data: { meat_withdrawal_days: d.meat_withdrawal_days, product_id: d.product_id, product_name: d.product_name } })) {
          const key = `${h.kind}:${d.product_id ?? h.product.toLowerCase()}`;
          const prev = holds.get(key);
          if (!prev || h.until >= prev.until) holds.set(key, h);
        }
        break;
      }
      case HEALTH_TYPES.sickness: {
        const d = e.data as SicknessData;
        const status = d.status ?? "sick";
        if (status === "recovered") { state.sickness = undefined; break; }
        state.sickness = state.sickness
          ? { ...state.sickness, status, checked: on || state.sickness.checked, condition: d.condition ?? state.sickness.condition, severity: d.severity ?? state.sickness.severity, recheck_on: d.recheck_on, pulled: d.pulled ?? state.sickness.pulled }
          : { since: on, status, condition: d.condition, severity: d.severity, checked: on, recheck_on: d.recheck_on, pulled: d.pulled, event_id: e.id };
        break;
      }
      case HEALTH_TYPES.sample: {
        const d = e.data as SampleData;
        samples.push({ test: d.test, on, sample_id: d.sample_id, event_id: e.id });
        break;
      }
      case HEALTH_TYPES.result: {
        const d = e.data as LabResultData;
        const match = samples.find((s) => !s.matched && (d.sample_id && s.sample_id ? s.sample_id === d.sample_id : s.test === d.test && s.on <= (on || s.on)));
        if (match) match.matched = true;
        if (!state.last_result || on >= state.last_result.on) state.last_result = { on, test: d.test, result: d.result, value: d.value, unit: d.unit, event_id: e.id };
        break;
      }
      case HEALTH_TYPES.bse: {
        const d = e.data as BseData;
        if (!state.bse || on >= state.bse.on) state.bse = { on, classification: d.classification, scrotal: d.scrotal, motility: d.motility, morphology: d.morphology, recheck_on: d.recheck_on, event_id: e.id };
        break;
      }
    }
  }
  state.holds = [...holds.values()].filter((h) => h.until > today).sort((a, b) => a.until.localeCompare(b.until));
  state.unresolved = unresolved;
  state.vaccinations = [...vaccinations.values()].sort((a, b) => b.on.localeCompare(a.on));
  state.procedures = [...procedures.values()].sort((a, b) => b.on.localeCompare(a.on));
  state.samples_waiting = samples.filter((s) => !s.matched).map(({ matched: _m, ...s }) => s);
  state.sick = !!state.sickness;
  return state;
}

const healthFacet = defineFacet<HealthState>({
  id: "health",
  subject: "animal",
  version: 1,
  eventTypes: Object.values(HEALTH_TYPES),
  reduce: (events, { today }) => reduceHealth(events, today),
  badges: (s, { today }) => {
    const out: Badge[] = [];
    const meat = longestHold(s, today, "meat");
    if (meat) out.push({ label: msg("Meat hold until {{date}}", { date: meat.until }), tone: "warn", icon: "shieldAlert" });
    const milk = longestHold(s, today, "milk");
    if (milk) out.push({ label: msg("Milk hold until {{date}}", { date: milk.until }), tone: "warn", icon: "shieldAlert" });
    if (!meat && s.unresolved?.length) out.push({ label: msg("Withdrawal not recorded"), tone: "warn", icon: "help" });
    if (s.sick) out.push({ label: s.sickness?.status === "improving" ? msg("Getting better") : msg("Sick"), tone: "bad", icon: "thermometer" });
    if (s.bse && s.bse.classification !== "satisfactory") out.push({ label: s.bse.classification === "deferred" ? msg("BSE deferred") : s.bse.classification === "unsatisfactory" ? msg("Failed BSE") : msg("BSE not classified"), tone: "warn", icon: "microscope" });
    return out;
  },
  facts: (s, { today }) => {
    const out: Fact[] = [];
    for (const h of activeHolds(s, today)) {
      const left = daysLeft(h.until, today);
      out.push({
        label: h.kind === "meat" ? "Meat hold" : "Milk hold", tone: "warn", icon: "shieldAlert",
        value: msg(left === 1 ? "Until {{date}} · {{product}} · 1 day left" : "Until {{date}} · {{product}} · {{days}} days left", { date: h.until, product: h.product, days: left }),
      });
    }
    if (s.sickness) out.push({ label: "Sick", tone: "bad", icon: "thermometer", value: msg(s.sickness.condition ? "Since {{date}} · {{condition}}" : "Since {{date}}", { date: s.sickness.since, condition: s.sickness.condition ?? "" }) });
    if (s.last_treatment) out.push({ label: "Last treatment", value: msg("{{product}} · {{date}}", { product: s.last_treatment.product, date: s.last_treatment.on }), icon: "syringe" });
    if (s.bse) {
      const when = { date: s.bse.on };
      const value = s.bse.classification === "satisfactory" ? msg("Satisfactory · {{date}}", when) : s.bse.classification === "deferred" ? msg("Deferred · {{date}}", when)
        : s.bse.classification === "unsatisfactory" ? msg("Unsatisfactory · {{date}}", when) : msg("Not classified · {{date}}", when);
      out.push({ label: "Breeding soundness", icon: "microscope", tone: s.bse.classification === "satisfactory" ? "good" : "warn", value });
    }
    return out;
  },
  filters: {
    sick: { label: "Sick", type: "boolean" },
  },
});

// ---------------------------------------------------------------- module

export default defineModule({
  id: "health",
  label: "Health",
  description: "Treatments, vaccines, protocols, withdrawal holds and diagnostics.",
  events: [treatment, vaccination, procedure, sickness, sample, labResult, bse],
  facets: [healthFacet],
  jobs: [{
    type: "health.round",
    label: "Treatment round",
    description: "The same shots or treatment for a set of cattle, one at a time at the chute or all at once.",
    icon: "syringe",
    tone: "health",
    workspace: "health",
    subjectKind: "animal",
    itemEvent: HEALTH_TYPES.treatment,
    agent: { when: "Someone wants to start or plan shots, a booster or a treatment for a group of cattle (\"start weaning shots on the calves\"). Put the protocol id in data.protocol_id when one fits." },
  }],
  workspaces: [{
    id: "health",
    label: "Manage health",
    description: "Sick cattle, shots and treatment rounds, protocols, withdrawal holds, lab results and bull exams.",
    icon: "syringe",
    group: "care",
    keywords: ["health", "treat", "treatment", "vaccinate", "vaccine", "shots", "sick", "withdrawal", "hold", "medicine", "booster", "protocol", "lab", "bse", "semen test", "bull test", "castrate", "deworm", "pinkeye", "foot rot", "vet"],
    keywordsEs: ["salud", "vacunar", "vacuna", "inyectar", "inyección", "enfermo", "enferma", "retiro", "medicina", "medicamento", "refuerzo", "tratamiento", "laboratorio", "prueba de toros", "castrar", "desparasitar", "veterinario"],
    presets: ["basic", "commercial", "feedlot", "dairy"],
  }],
  stockKinds: [
    { kind: "medicine", label: "Medicine", fields: productFields() },
    { kind: "vaccine", label: "Vaccine", fields: productFields() },
  ],
  // Strings the shell renders with t() from this module's web definition, and quick reasons.
  messages: [
    "Meat hold", "Milk hold", "Sick", "Last treatment", "Breeding soundness",
    "Health", "Recheck", "Treat", "Vaccinate", "Note sickness", "Bull exam (BSE)", "Castrate", "Label and withdrawal", "Given to",
    "Treatments", "Vaccinations", "Sickness and checks", "Procedures", "Lab work", "Bull exams",
    ...SICK_REASONS,
    // Chosen at runtime (singular or plural, meat or milk); listed so both forms are translated.
    "Until {{date}} · {{product}} · 1 day left", "Until {{date}} · {{product}} · {{days}} days left",
    "Since {{date}} · {{condition}}", "Since {{date}}",
    "Not checked in {{count}} day", "Not checked in {{count}} days", "Retest bull {{animal}}", "Recheck {{animal}}",
    "Meat hold ends: {{animal}}", "Milk hold ends: {{animal}}", "Meat hold ends: {{count}} head", "Milk hold ends: {{count}} head",
    "{{count}} treatment has no withdrawal recorded", "{{count}} treatments have no withdrawal recorded",
    "{{count}} lab sample has no result after 2 weeks", "{{count}} lab samples have no results after 2 weeks",
    "{{product}} lot {{lot}} has expired", "{{product}} lot {{lot}} expires soon",
  ],
});

function productFields(): Fields {
  return {
    active_ingredient: f.text("Active ingredient", { placeholder: "Oxytetracycline" }),
    strength: f.text("Strength", { placeholder: "200 mg/mL" }),
    label_dose: f.quantity("Label dose", "dose", { units: DOSE_UNITS }),
    label_dose_per: f.quantity("Per body weight", "weight", { help: "Leave empty when the label dose is per head. 100 lb for \"per 100 lb\"." }),
    route: f.enum("Label route", ROUTES),
    max_per_site: f.quantity("Most per injection site", "volume", { level: "more" }),
    meat_withdrawal_days: f.int("Meat withdrawal (days)", { min: 0, max: 400, help: "From the label. 0 means none." }),
    milk_withdrawal_hours: f.int("Milk withdrawal (hours)", { min: 0, max: 2000, help: "Leave empty when the label gives none." }),
    booster_days: f.int("Booster after (days)", { min: 1, max: 730 }),
    storage: f.text("Storage", { level: "more", placeholder: "Keep cold, 35–46 °F" }),
    maker: f.text("Maker", { level: "more" }),
    label_notes: f.longText("Label notes", { level: "more", placeholder: "Not for lactating dairy cattle" }),
  };
}

export type { HealthState, HealthHold };
